import { Card, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@libs/zapi-ui/shadcn";
import { PaginationControls } from "@/zapi-admin/pages/AiLogsPage/ui/PaginationControls";
import { TAuthLogItem } from "../model/types";
import { CalendarBlank } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";

type TAuthLogsTableProps = {
  logs: TAuthLogItem[];
  total?: number;
  page?: number;
  pageSize?: number;
  setPage?: (page: number) => void;
};

const eventColors: Record<string, { bg: string; text: string }> = {
  login: { bg: "bg-green-50", text: "text-green-700" },
  logout: { bg: "bg-zinc-100", text: "text-zinc-700" },
  signup: { bg: "bg-blue-50", text: "text-blue-700" },
  user_deactivated: { bg: "bg-red-50", text: "text-red-700" }
};

const sourceColors: Record<string, { bg: string; text: string }> = {
  platform: { bg: "bg-purple-50", text: "text-purple-700" },
  admin_panel: { bg: "bg-orange-50", text: "text-orange-700" }
};

export function AuthLogsTable({ logs, total = 0, page = 1, setPage, pageSize = 10 }: TAuthLogsTableProps) {
  const truncateUserAgent = (ua: string | null) => {
    if (!ua) return "-";
    return ua.length > 40 ? ua.substring(0, 40) + "..." : ua;
  };

  return (
    <Card className="py-0 rounded-[10px] border-zinc-150 border gap-0" data-testid="auth-logs-table-card">
      <Table className="p-[12px] text-zinc-500 text-[10px]" data-testid="auth-logs-table">
        <TableHeader className="p-[12px] text-zinc-500 text-[10px]">
          <TableRow>
            <TableHead className="min-w-[150px] text-zinc-600 text-[14px] capitalize">Timestamp</TableHead>
            <TableHead className="min-w-[200px] text-zinc-600 text-[14px] capitalize">User</TableHead>
            <TableHead className="min-w-[100px] text-zinc-600 text-[14px] capitalize">Event</TableHead>
            <TableHead className="min-w-[120px] text-zinc-600 text-[14px] capitalize">IP</TableHead>
            <TableHead className="min-w-[150px] text-zinc-600 text-[14px] capitalize">Location</TableHead>
            <TableHead className="min-w-[100px] text-zinc-600 text-[14px] capitalize">Source</TableHead>
            <TableHead className="min-w-[200px] text-zinc-600 text-[14px] capitalize">User Agent</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {logs.map((log) => {
            const eventColor = eventColors[log.event] || { bg: "bg-zinc-100", text: "text-zinc-700" };
            const sourceColor = sourceColors[log.source] || { bg: "bg-zinc-100", text: "text-zinc-700" };

            return (
              <TableRow key={log.id} className="group transition hover:bg-zinc-50">
                <TableCell className="font-medium min-h-[56px]">
                  <Badge variant="secondary" className="flex items-center gap-1 rounded-[4px] bg-zinc-100 px-[4px] w-fit">
                    <CalendarBlank size={14} className="text-zinc-400" />
                    {new Date(log.createdAt).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true
                    })}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium min-h-[56px]">
                  <FigmaTypography variant="label-2" className="text-zinc-900">
                    {log.user.firstName} {log.user.lastName}
                  </FigmaTypography>
                  <FigmaTypography variant="label-2" className="text-zinc-500 text-[12px] leading-[18px]">
                    {log.user.email}
                  </FigmaTypography>
                </TableCell>
                <TableCell className="font-medium min-h-[56px]">
                  <Badge className={`rounded-[12px] ${eventColor.bg} ${eventColor.text} hover:${eventColor.bg}`}>{log.event}</Badge>
                </TableCell>
                <TableCell className="font-medium min-h-[56px]">
                  <FigmaTypography variant="label-2" className="text-zinc-500">
                    {log.ip || "-"}
                  </FigmaTypography>
                </TableCell>
                <TableCell className="font-medium min-h-[56px]">
                  <FigmaTypography variant="label-2" className="text-zinc-500">
                    {log.location || "-"}
                  </FigmaTypography>
                </TableCell>
                <TableCell className="font-medium min-h-[56px]">
                  <Badge className={`rounded-[12px] ${sourceColor.bg} ${sourceColor.text} hover:${sourceColor.bg}`}>
                    {log.source === "admin_panel" ? "admin panel" : log.source}
                  </Badge>
                </TableCell>
                <TableCell className="font-medium min-h-[56px]">
                  <FigmaTypography variant="label-2" className="text-zinc-500" title={log.userAgent || undefined}>
                    {truncateUserAgent(log.userAgent)}
                  </FigmaTypography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <PaginationControls page={page} total={total} pageSize={pageSize} setPage={setPage} />
    </Card>
  );
}
